/**
 * Calendar's full-results view (agora#269): every event
 * `CalendarClient.searchEvents` finds for a query, drawn by
 * `SearchResultsPage`. `Calendar.tsx` renders this instead of the week view
 * whenever `location.query` is set -- reached at
 * `/h/<agoraId>/calendar/search/<query>`, which is what Enter opens from the
 * header search when nothing is highlighted (`search/SearchField.tsx`).
 *
 * Runs its own request rather than reusing the header panel's: the panel
 * caps at 20 rows (`search/model.ts`'s `DEFAULT_LIMIT`) and this wants
 * every match.
 */
import type { CalendarClient } from '@wtfalch/calendar/calendar';
import { useEffect, useState } from 'react';
import { searchCalendarEvents } from '../search/calendar.ts';
import { calendarErrorMessage } from '../search/model.ts';
import { SearchResultsPage } from '../search/SearchResultsPage.tsx';
import type { SearchRow } from '../search/types.ts';

const LIMIT = 50;

export function CalendarSearchResults({
  client,
  agoraId,
  query,
  onOpen,
}: {
  client: CalendarClient;
  agoraId: string;
  query: string;
  onOpen: (row: SearchRow) => void;
}) {
  const [rows, setRows] = useState<readonly SearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    searchCalendarEvents(client, agoraId, query, { limit: LIMIT })
      .then((outcome) => {
        if (!live) return;
        setRows(outcome.rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(calendarErrorMessage(err));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, agoraId, query]);

  return (
    <SearchResultsPage
      query={query}
      icon="calendar"
      loading={loading}
      error={error}
      rows={rows}
      emptyLabel={`No events found for “${query}”.`}
      onOpen={onOpen}
    />
  );
}
